import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withNamespaces } from "react-i18next";
import Board from "@asseinfo/react-kanban";
import { Col, Row } from "reactstrap";
import { toast } from "react-toastify";
import { EMPTY_LIST } from "src/common/constants";
import ColumnHeader from "./components/ColumnHeader";
import TaskCardItem from "./components/TaskCardItem";
import ColumnPaginationSentinel from "./components/ColumnPaginationSentinel";
import { DEFAULT_TASK_PRIORITIES } from "./components/constants";
import {
    DEFAULT_COLUMN_STATUSES,
    DEFAULT_PAGE_SIZE,
    buildColumnFromPayload,
    defaultMapTaskContent,
    getBoardTotalCount,
    mapBoardApiDataToColumns,
    toBoardColumns,
} from "./helpers";
import "./kanban-board.scss";

/**
 * Shared Kanban Board with per-column paginated loading.
 *
 * fetchBoardData({ ...filters, status, pageIndex, pageSize })
 * is called once per column on load / filter change, then again when
 * the user scrolls to the bottom (next) or top (previous) of a column.
 */
const KanbanBoard = ({
    t,
    // Data
    fetchBoardData,
    queryKey = ["shared-kanban-board"],
    columnStatuses = DEFAULT_COLUMN_STATUSES,
    pageSize = DEFAULT_PAGE_SIZE,
    mapTaskContent = defaultMapTaskContent,
    initialFilters = {},
    // Presentation
    className = "",
    boardWrapperClassName = "d-flex",
    // Card customization
    statuses = {},
    priorities = DEFAULT_TASK_PRIORITIES,
    getTaskLink,
    onTaskLinkClick,
    renderTaskId,
    renderSubtitle,
    renderStatus,
    renderPriority,
    TaskCardComponent = TaskCardItem,
    renderCard,
    renderColumnHeader,
    // Filters — module owns the FilteringComponent
    showFilters = true,
    FilteringComponent = null,
    FilteringComponentProps = {},
    renderFilters,
    // Empty / loading
    showEmptyState = false,
    emptyMessage = EMPTY_LIST,
    loadingColumnCount = 4,
}) => {
    const [filters, setFilters] = useState(initialFilters);
    const [columnsState, setColumnsState] = useState([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [boardEpoch, setBoardEpoch] = useState(0);

    const fetchBoardDataRef = useRef(fetchBoardData);
    const mapTaskContentRef = useRef(mapTaskContent);
    const filtersRef = useRef(filters);
    const statusesRef = useRef(statuses);
    const pageSizeRef = useRef(pageSize);
    const inFlightRef = useRef({});
    const columnsStateRef = useRef(columnsState);

    useEffect(() => {
        fetchBoardDataRef.current = fetchBoardData;
    }, [fetchBoardData]);

    useEffect(() => {
        mapTaskContentRef.current = mapTaskContent;
    }, [mapTaskContent]);

    useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);

    useEffect(() => {
        statusesRef.current = statuses;
    }, [statuses]);

    useEffect(() => {
        pageSizeRef.current = pageSize;
    }, [pageSize]);

    useEffect(() => {
        columnsStateRef.current = columnsState;
    }, [columnsState]);

    const resolvedColumnStatuses = useMemo(() => {
        if (Array.isArray(columnStatuses) && columnStatuses.length > 0) {
            return columnStatuses;
        }

        return DEFAULT_COLUMN_STATUSES;
    }, [columnStatuses]);

    const filtersKey = useMemo(() => JSON.stringify(filters || {}), [filters]);
    const statusesKey = useMemo(
        () => resolvedColumnStatuses.join("|"),
        [resolvedColumnStatuses]
    );
    const queryKeyValue = useMemo(
        () => (Array.isArray(queryKey) ? queryKey.join("|") : String(queryKey)),
        [queryKey]
    );

    const board = useMemo(
        () => ({
            columns: toBoardColumns(columnsState),
        }),
        [columnsState]
    );

    const totalCount = useMemo(() => getBoardTotalCount(columnsState), [columnsState]);

    const updateColumn = useCallback((status, updater) => {
        setColumnsState((prev) =>
            prev.map((column) => {
                if (column.status !== status) {
                    return column;
                }

                return typeof updater === "function" ? updater(column) : updater;
            })
        );
    }, []);

    const fetchColumnPage = useCallback(async (status, pageIndex) => {
        const fetchFn = fetchBoardDataRef.current;

        if (typeof fetchFn !== "function") {
            return null;
        }

        const payload = await fetchFn({
            ...filtersRef.current,
            status,
            pageIndex,
            pageSize: pageSizeRef.current,
        });

        return buildColumnFromPayload({
            status,
            payload,
            mapTaskContent: mapTaskContentRef.current,
            fallbackTitle: statusesRef.current?.[status]?.title || status,
        });
    }, []);

    // Initial / filter-reset load: one request per column, pageIndex = 1
    useEffect(() => {
        let cancelled = false;

        const loadColumns = async () => {
            if (typeof fetchBoardDataRef.current !== "function") {
                setColumnsState([]);
                setIsInitialLoading(false);
                return;
            }

            setIsInitialLoading(true);
            inFlightRef.current = {};

            try {
                const results = await Promise.all(
                    resolvedColumnStatuses.map(async (status) => {
                        try {
                            return await fetchColumnPage(status, 1);
                        } catch (error) {
                            if (process.env.NODE_ENV === "development") {
                                console.error(error);
                            }

                            return buildColumnFromPayload({
                                status,
                                payload: {
                                    status,
                                    title: statusesRef.current?.[status]?.title || status,
                                    count: 0,
                                    data: [],
                                    pageIndex: 1,
                                    pageSize: pageSizeRef.current,
                                    hasMore: false,
                                },
                                mapTaskContent: mapTaskContentRef.current,
                                fallbackTitle: statusesRef.current?.[status]?.title || status,
                            });
                        }
                    })
                );

                if (cancelled) {
                    return;
                }

                setColumnsState(results.filter(Boolean));
                setBoardEpoch((value) => value + 1);
            } catch (error) {
                if (process.env.NODE_ENV === "development") {
                    console.error(error);
                }

                if (!cancelled) {
                    toast(t("An error occurred while fetching tasks list."), {
                        type: "error",
                    });
                    setColumnsState([]);
                }
            } finally {
                if (!cancelled) {
                    setIsInitialLoading(false);
                }
            }
        };

        loadColumns();

        return () => {
            cancelled = true;
        };
    }, [filtersKey, statusesKey, pageSize, queryKeyValue, fetchColumnPage, resolvedColumnStatuses, t]);

    const loadNextPage = useCallback(
        async (status) => {
            const column = columnsStateRef.current.find((item) => item.status === status);
            const flightKey = `${status}:next`;

            if (
                !column ||
                !column.hasMore ||
                column.loadingNext ||
                inFlightRef.current[flightKey]
            ) {
                return;
            }

            inFlightRef.current[flightKey] = true;
            updateColumn(status, (current) => ({ ...current, loadingNext: true }));

            try {
                const nextPageIndex = column.pageIndex + 1;
                const nextColumn = await fetchColumnPage(status, nextPageIndex);
                const nextCards = nextColumn?.cards || [];
                const currentPageSize = nextColumn?.pageSize || column.pageSize || pageSizeRef.current;

                updateColumn(status, (current) => {
                    const existingIds = new Set(current.cards.map((card) => card.id));
                    const cards = [
                        ...current.cards,
                        ...nextCards.filter((card) => !existingIds.has(card.id)),
                    ];

                    return {
                        ...current,
                        count: nextColumn?.count ?? current.count,
                        columnsubtitle: `${nextColumn?.count ?? current.count}`,
                        pageIndex: nextColumn?.pageIndex ?? nextPageIndex,
                        pageSize: currentPageSize,
                        hasMore: nextColumn?.hasMore ?? false,
                        loadingNext: false,
                        cards,
                    };
                });

                // Nudge the page up so the bottom sentinel leaves the viewport.
                // User must scroll down again to load the following page.
                if (typeof window !== "undefined" && nextCards.length > 0) {
                    requestAnimationFrame(() => {
                        window.scrollBy({ top: -160, left: 0, behavior: "auto" });
                    });
                }
            } catch (error) {
                if (process.env.NODE_ENV === "development") {
                    console.error(error);
                }

                toast(t("An error occurred while fetching tasks list."), {
                    type: "error",
                });
                updateColumn(status, (current) => ({ ...current, loadingNext: false }));
            } finally {
                inFlightRef.current[flightKey] = false;
            }
        },
        [fetchColumnPage, t, updateColumn]
    );

    const handleFiltersChange = useCallback((newFilters) => {
        // Pagination resets to page 1 because the filters effect reloads columns.
        setFilters((prevFilters) => ({
            ...prevFilters,
            ...newFilters,
        }));
    }, []);

    const renderLoadingSection = () => {
        return (
            <Row>
                {Array.from({ length: loadingColumnCount }).map((_, index) => (
                    <Col sm="12" md="3" key={`kanban-skeleton-${index}`}>
                        <div className="dt-field dt-skeleton mb-2" style={{ width: "50%" }} />
                        <div
                            className="dt-field dt-skeleton"
                            style={{ width: "100%", height: "500px" }}
                        />
                    </Col>
                ))}
            </Row>
        );
    };

    const renderDefaultCard = (content) => {
        const CardComponent = TaskCardComponent;

        return (
            <CardComponent
                data={content}
                statuses={statuses}
                priorities={priorities}
                getTaskLink={getTaskLink}
                onTaskLinkClick={onTaskLinkClick}
                renderTaskId={renderTaskId}
                renderSubtitle={renderSubtitle}
                renderStatus={renderStatus}
                renderPriority={renderPriority}
            />
        );
    };

    const renderBoardCard = (content) => {
        if (content?.__pagination) {
            const column = columnsState.find((item) => item.status === content.status);

            return (
                <ColumnPaginationSentinel
                    status={content.status}
                    direction="next"
                    disabled={!column?.hasMore}
                    loading={!!column?.loadingNext}
                    label={t("Loading")}
                    onVisible={() => {
                        loadNextPage(content.status);
                    }}
                />
            );
        }

        if (typeof renderCard === "function") {
            return renderCard(content);
        }

        return renderDefaultCard(content);
    };

    const renderBoard = () => {
        if (showEmptyState && totalCount === 0) {
            return (
                <div className="alert alert-warning" role="alert">
                    <p
                        style={{
                            textAlign: "center",
                            marginBottom: 0,
                            width: "100%",
                        }}
                    >
                        {t(emptyMessage)}
                    </p>
                </div>
            );
        }

        return (
            <div className={boardWrapperClassName}>
                <Board
                    key={`kanban-board-${boardEpoch}-${filters?.sortBy || "default"}`}
                    allowAddCard={false}
                    disableColumnDrag={true}
                    disableCardDrag={true}
                    renderColumnHeader={(column) => {
                        if (typeof renderColumnHeader === "function") {
                            return renderColumnHeader(column);
                        }

                        return (
                            <ColumnHeader
                                title={column.title}
                                subtitle={column.columnsubtitle}
                            />
                        );
                    }}
                    renderCard={({ content }) => renderBoardCard(content)}
                    onNewCardConfirm={(draftCard) => ({
                        id: new Date().getTime(),
                        ...draftCard,
                    })}
                >
                    {board}
                </Board>
            </div>
        );
    };

    const renderFiltersSection = () => {
        if (!showFilters) {
            return null;
        }

        if (typeof renderFilters === "function") {
            return renderFilters(handleFiltersChange, filters);
        }

        if (!FilteringComponent) {
            return null;
        }

        return (
            <FilteringComponent
                onChange={handleFiltersChange}
                {...FilteringComponentProps}
            />
        );
    };

    return (
        <div className={`shared-kanban-board ${className}`.trim()}>
            {showFilters ? (
                <Row className="mb-4">
                    <Col sm="12" className="d-flex justify-content-end">
                        {renderFiltersSection()}
                    </Col>
                </Row>
            ) : null}

            <Row>
                <Col sm="12">
                    {isInitialLoading ? renderLoadingSection() : renderBoard()}
                </Col>
            </Row>
        </div>
    );
};

export default withNamespaces()(KanbanBoard);

export {
    ColumnHeader,
    TaskCardItem,
    defaultMapTaskContent,
    mapBoardApiDataToColumns,
    getBoardTotalCount,
    DEFAULT_TASK_PRIORITIES,
    DEFAULT_COLUMN_STATUSES,
    DEFAULT_PAGE_SIZE,
};
